import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { LuWorkflow } from 'react-icons/lu';

import { Strategy, StrategyType } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

import { formatWeightForDisplay, formatWeightForSave } from '../shared-utils';
import { createFormSchema, FormValues } from './strategy-form-schema';
import { isBlackrockStrategy, StrategyDraft } from './strategy-utils';
import { getStrategyDescription } from './strategyDescriptions';
import { StrategyFormBody } from './StrategyFormBody';
import { useAssetTableProps } from './useAssetTableProps';

interface EditStrategyDialogProps {
    strategy?: Strategy | StrategyDraft;
    onSave: (data: FormValues) => void;
    onCancel: () => void;
    open: boolean;
    isSaving?: boolean;
    canCreateGlobalStrategy?: boolean;
    existingStrategyNames?: string[];
    readOnly?: boolean;
}

export function EditStrategyDialog({
    strategy,
    onSave,
    onCancel,
    open,
    isSaving = false,
    canCreateGlobalStrategy = false,
    existingStrategyNames = [],
    readOnly = false,
}: EditStrategyDialogProps) {
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const getDefaultValues = useCallback(
        (strategyData?: Strategy | StrategyDraft): FormValues => ({
            strategyName: strategyData?.strategyName || '',
            strategyType: (strategyData?.strategyType || StrategyType.PERSONAL) as
                | StrategyType.PERSONAL
                | StrategyType.SHARED,
            assetAllocations: strategyData?.assetAllocations
                ? strategyData.assetAllocations
                      .map((asset) => ({
                          ...asset,
                          weight: formatWeightForDisplay(asset.weight),
                      }))
                      .sort((a, b) => parseFloat(b.weight) - parseFloat(a.weight))
                : [],
        }),
        [],
    );

    const formSchema = createFormSchema(existingStrategyNames, strategy?.strategyName);

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(strategy),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(strategy));
    }, [strategy, form, getDefaultValues]);

    useEffect(() => {
        if (open) {
            form.reset(getDefaultValues(strategy));
            if (!readOnly) {
                const timer = setTimeout(() => {
                    nameInputRef.current?.focus();
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [open, strategy, form, getDefaultValues, readOnly]);

    const assetTableProps = useAssetTableProps(form);

    const handleSave = (data: FormValues) => {
        const normalizedData = {
            ...data,
            assetAllocations: data.assetAllocations.map((asset) => ({
                ...asset,
                weight: formatWeightForSave(asset.weight),
            })),
        };
        onSave(normalizedData);
    };

    const isAdding = !strategy?.strategyGUID;
    const isCoreStrategy = strategy?.strategyType === StrategyType.CORE;
    const coreStrategyDescription =
        isCoreStrategy && strategy.growthAllocationType
            ? getStrategyDescription(strategy.growthAllocationType)
            : undefined;
    const coreAssetDetails = coreStrategyDescription ? (
        <div className="border-cocoa-200 text-cocoa-700 rounded-lg border bg-white p-6 text-sm">
            <h4 className="text-cocoa-500 mb-3 text-xs font-semibold tracking-wide uppercase">Description</h4>
            {coreStrategyDescription}
        </div>
    ) : isCoreStrategy && strategy.assetAllocations.length === 0 ? (
        <div className="border-cocoa-200 text-cocoa-500 rounded-lg border border-dashed bg-white px-6 py-10 text-center text-sm">
            No holdings information available.
        </div>
    ) : undefined;
    const readOnlyTitle =
        strategy && isBlackrockStrategy(strategy) ? 'BlackRock Investment Strategy' : 'Claro Investment Strategy';
    const modalTitle = readOnly ? readOnlyTitle : isAdding ? 'New Strategy' : 'Edit Strategy';

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
            <DialogContent
                className="flex max-h-[85vh] max-w-6xl flex-col bg-white tracking-[-0.02em]"
                onOpenAutoFocus={(event) => readOnly && event.preventDefault()}
            >
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <LuWorkflow className="h-5 w-5 text-gray-600" />
                        {modalTitle}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 min-h-0 flex-auto overflow-y-auto px-1">
                    <form id="strategy-form" onSubmit={form.handleSubmit(handleSave)}>
                        <StrategyFormBody
                            form={form}
                            showTypeSelector={canCreateGlobalStrategy}
                            canChangeType={isAdding}
                            nameInputRef={nameInputRef}
                            readOnly={readOnly}
                            assetDetailsOverride={coreAssetDetails}
                            {...assetTableProps}
                        />
                    </form>
                </div>
                <DialogFooter className="shrink-0">
                    <Button
                        variant="outline"
                        className="font-medium"
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        {readOnly ? 'Close' : 'Cancel'}
                    </Button>
                    {!readOnly && (
                        <Button
                            variant="default"
                            type="submit"
                            className="font-medium"
                            form="strategy-form"
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
